import { z } from 'zod'
import { createdAtSortQueryShape, creatorListQueryShape } from '../list-query/contract.ts'
import { accountDisplaySchema } from '../profile/contract.ts'

export const roomTypeSchema = z.enum(['office', 'workstation', 'warehouse', 'other'])
export const roomStatusSchema = z.enum(['vacant', 'reserved', 'occupied', 'maintenance', 'disabled'])
export const roomListSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(255).optional(),
  siteId: z.number().int().positive().optional(),
  status: roomStatusSchema.optional(),
  roomType: roomTypeSchema.optional(),
  ...creatorListQueryShape,
  ...createdAtSortQueryShape,
  sortField: z.enum(['createdAt', 'roomNo']).optional()
})
export const roomTreeSchema = z.object({
  siteId: z.number().int().positive()
})
export const roomRecommendationSchema = z.object({
  siteId: z.number().int().positive(),
  businessId: z.number().int().positive().optional()
})
export const roomAvailableOptionsSchema = z.object({
  siteId: z.number().int().positive(),
  occupancyMode: z.enum(['physical', 'registration']).default('physical'),
  startAt: z.coerce.date(),
  endAt: z.coerce.date()
})
const roomBaseSchema = z.object({
  siteId: z.number().int().positive(),
  roomNo: z.string().trim().min(1, '房间号不能为空').max(50),
  floor: z.string().trim().max(50).optional().nullable(),
  roomType: roomTypeSchema,
  area: z.number().min(0).max(999_999.99).optional().nullable(),
  status: roomStatusSchema,
  note: z.string().trim().max(2000).optional().nullable()
})
export const roomUpsertSchema = roomBaseSchema
export const roomUpsertRequestSchema = z.object({
  id: z.number().int().positive().optional(),
  data: roomUpsertSchema
})
export const roomBatchItemSchema = z.object({
  roomNo: z.string().trim().min(1, '房间号不能为空').max(50),
  area: z.number().min(0).max(999_999.99).optional().nullable()
})
export const roomBatchCreateSchema = z.object({
  siteId: z.number().int().positive(),
  rooms: z.array(roomBatchItemSchema).min(1, '至少添加一个房间').max(500, '单次最多生成 500 个房间'),
  floor: z.string().trim().max(50).optional().nullable(),
  roomType: roomTypeSchema,
  status: roomStatusSchema,
  note: z.string().trim().max(2000).optional().nullable()
})
export const roomSetStatusSchema = z.object({
  id: z.number().int().positive(),
  status: roomStatusSchema
})
const roomIdsSchema = z
  .array(z.number().int().positive())
  .min(1, '至少选择一个房间')
  .max(100, '单次最多处理 100 个房间')
export const roomBatchSetStatusSchema = z.object({
  ids: roomIdsSchema,
  status: roomStatusSchema
})
export const roomRemoveSchema = z.object({
  id: z.number().int().positive()
})
export const roomDetailSchema = roomRemoveSchema
export const roomBatchRemoveSchema = z.object({ ids: roomIdsSchema })
export const roomSchema = roomBaseSchema.extend({
  id: z.number().int().positive(),
  fullAddress: z.string(),
  site: z.object({
    id: z.number().int().positive(),
    name: z.string(),
    organization: z
      .object({
        id: z.number().int().positive(),
        name: z.string()
      })
      .nullable()
  }),
  area: z.string().nullable(),
  note: z.string().nullable(),
  floor: z.string().nullable(),
  creator: accountDisplaySchema,
  createdAt: z.date(),
  updatedAt: z.date()
})
export const roomTreeRoomSchema = z.object({
  id: z.number().int().positive(),
  siteId: z.number().int().positive(),
  roomNo: z.string(),
  floor: z.string().nullable(),
  roomType: roomTypeSchema,
  area: z.string().nullable(),
  status: roomStatusSchema,
  note: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
})
export const roomTreeOutputSchema = z.object({
  site: z.object({
    id: z.number().int().positive(),
    name: z.string(),
    organization: z
      .object({
        id: z.number().int().positive(),
        name: z.string()
      })
      .nullable()
  }),
  rooms: z.array(roomTreeRoomSchema)
})
export const roomBatchCreateResultSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.array(z.string()),
  total: z.number().int().nonnegative()
})
export const roomBatchOperationResultSchema = z.object({ count: z.number().int().nonnegative() })
export const roomListOutputSchema = z.object({
  data: z.array(roomSchema),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})
export const roomRecommendationOutputSchema = z.object({
  room: roomSchema.nullable(),
  reason: z.string()
})
export type RoomRecommendationInput = z.infer<typeof roomRecommendationSchema>
export const siteOptionSchema = z.object({
  label: z.string(),
  value: z.number().int().positive()
})
export const roomAvailableOptionSchema = z.object({
  id: z.number().int().positive(),
  siteId: z.number().int().positive(),
  roomNo: z.string(),
  floor: z.string().nullable(),
  roomType: roomTypeSchema,
  area: z.string().nullable(),
  status: roomStatusSchema,
  organization: z.object({ id: z.number().int().positive(), name: z.string() }).nullable()
})
export type RoomType = z.infer<typeof roomTypeSchema>
export type RoomStatus = z.infer<typeof roomStatusSchema>
export type RoomListInput = z.infer<typeof roomListSchema>
export type RoomTreeInput = z.infer<typeof roomTreeSchema>
export type RoomAvailableOptionsInput = z.infer<typeof roomAvailableOptionsSchema>
export type RoomUpsertInput = z.infer<typeof roomUpsertSchema>
export type RoomBatchCreateInput = z.infer<typeof roomBatchCreateSchema>
export type RoomSetStatusInput = z.infer<typeof roomSetStatusSchema>
export type RoomBatchSetStatusInput = z.infer<typeof roomBatchSetStatusSchema>
export type RoomRemoveInput = z.infer<typeof roomRemoveSchema>
export type RoomBatchRemoveInput = z.infer<typeof roomBatchRemoveSchema>
